import { ChangeEvent, FocusEvent, useEffect, useMemo, useState } from "react";
import { Logger } from "@utils/logger";
import { FormikErrors, FormikTouched, getIn } from "formik";
import { useParamSelector } from "@hooks/use-param-selector";
import { shouldHighlightInput } from "@redux/selectors/verification-form-selectors";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { useSelector } from "react-redux";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { GoogleAddressComponentDisplay, SelectOption } from "@iddataweb/idw-common-ui/types";
import { GOOGLE_ADDRESS_COMPONENT_SELECT_OPTIONS_MAP } from "@iddataweb/idw-common-ui/constants";
import { InternationalAddressCountryTypes } from "@iddataweb/idw-common-ui/enums";
import {
    AXNUISelectInputWithErrorMessage,
    AXNUITextInputWithErrorMessage
} from "@iddataweb/idw-common-ui/containers";
import styles from "./address-component-input.less";

type AddressComponentInputProps = {
    countryCode: InternationalAddressCountryTypes;
    addressComponent: GoogleAddressComponentDisplay;
    attribute: AttributeFormState;
    touched: FormikTouched<any>;
    errors: FormikErrors<any>;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    onSetSubmitValue: (data: any) => void;
    isSubmitting: boolean;
};

export const AddressComponentInput = (props: AddressComponentInputProps) => {
    const {
        countryCode,
        addressComponent,
        attribute,
        touched,
        errors,
        onBlur,
        setFieldTouched,
        setFieldValue,
        onSetSubmitValue,
        isSubmitting
    } = props;

    const content = useSelector(getLanguageContent);

    const [selectedOption, setSelectedOption] = useState(null);

    const selectOptions: Array<SelectOption> = useMemo(() => {
        if (addressComponent.type === "select") {
            return GOOGLE_ADDRESS_COMPONENT_SELECT_OPTIONS_MAP[countryCode][addressComponent.name] || [];
        }

        return [];
    }, [countryCode, addressComponent.name])

    useEffect(() => {
        if (addressComponent.type === "select") {
            const option = selectOptions.find(state => state.value === attribute.inputData[addressComponent.name]);
            if (option) {
                setSelectedOption(option);
            } else {
                setSelectedOption(null);
            }
        }
    }, [selectOptions, attribute.inputData[addressComponent.name]]);

    const handleSelectChange = async (newValue: SelectOption) => {
        Logger.info(`[InternationAddress${addressComponent.name}] submit value: `, newValue);
        if (newValue && newValue.value !== "--") {
            const { value } = newValue;
            const inputData = { ...attribute.inputData, [addressComponent.name]: value };
            onSetSubmitValue({ inputData });
            await setFieldValue(addressComponent.name, value);
            setFieldTouched(addressComponent.name, true, true);
        }
    };

    const handleTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        Logger.info(`[InternationAddress${addressComponent.name}Input] submit value: `, value);
        const inputData = { ...attribute.inputData, [addressComponent.name]: value };
        onSetSubmitValue({ inputData });
        await setFieldValue(addressComponent.name, value);
        setFieldTouched(addressComponent.name, true, true);
    };

    const fieldTouched = getIn(touched, addressComponent.name);
    const fieldError = getIn(errors, addressComponent.name);
    const fieldIsInvalid = fieldTouched && fieldError;
    const fieldShouldBeHighlighted = useParamSelector(shouldHighlightInput, attribute);

    return (
        <div className={styles.addressComponentInputContainer}>
            {addressComponent.type === "select" && (
                <AXNUISelectInputWithErrorMessage
                    controlId={`attr${attribute.id}-inputs-${addressComponent.name}`}
                    automationId={`attr${attribute.id}-inputs-${addressComponent.name}`}
                    ariaLabel={content.validation.internationaladdress[addressComponent.name].required}
                    isClearable={false}
                    isSearchable={true}
                    title={content.validation.internationaladdress[addressComponent.name].required}
                    placeholder={content.form.internationalAddress.placeholders[addressComponent.name]}
                    selectedOption={selectedOption}
                    options={selectOptions}
                    onChange={handleSelectChange}
                    fieldName={addressComponent.name}
                    fieldError={fieldError}
                    fieldTouched={fieldTouched}
                    fieldIsInvalid={fieldIsInvalid}
                    fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                    onBlur={onBlur}
                    setFieldTouched={setFieldTouched}
                    isSubmitting={isSubmitting}
                    isDisabled={attribute.isDisabled}
                    isOptional={attribute.isOptional}
                    isPlaceholder={attribute.isPlaceholder}
                />
            )}
            {addressComponent.type === "text" && (
                <AXNUITextInputWithErrorMessage
                    controlId={`attr${attribute.id}-inputs-${addressComponent.name}`}
                    automationId={`attr${attribute.id}-inputs-${addressComponent.name}`}
                    autocomplete={"new-password"}
                    fieldName={addressComponent.name}
                    fieldError={fieldError}
                    fieldTouched={fieldTouched}
                    fieldIsInvalid={fieldIsInvalid}
                    fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                    onBlur={onBlur}
                    setFieldTouched={setFieldTouched}
                    isSubmitting={isSubmitting}
                    isDisabled={attribute.isDisabled}
                    isOptional={attribute.isOptional}
                    isPlaceholder={attribute.isPlaceholder}
                    onChange={handleTextChange}
                    title={content.validation.internationaladdress[addressComponent.name].required}
                    placeholder={content.form.internationalAddress.placeholders[addressComponent.name]}
                    value={attribute.inputData[addressComponent.name]}
                />
            )}
        </div>
    );
};